import React, { useState, useCallback, useEffect, useRef } from "react";
import mockCss from "../Mock.module.css";
import axios from "axios";
import { Vortex, Oval } from 'react-loader-spinner';
import { ButtonThin, ButtonSubmitGreen } from "../../UI/Buttons/Buttons";
import TutorialPopover from "../../UI/TutorialPopover/TutorialPopover";
import { getTutorialTipMeta } from '../../../utils/tutorialApi';
import { FaGraduationCap, FaBolt, FaProjectDiagram, FaChartPie, FaBookOpen, FaRobot, FaLightbulb, FaUsers, FaSuitcase, FaChartLine, FaTrophy, FaCalendarAlt, FaCheckCircle, FaTimesCircle, FaDownload, FaMedal, FaVideo, FaHourglass, FaBookmark, FaRegBookmark, FaChevronDown, FaChevronUp } from "react-icons/fa";
import { FaLock } from "react-icons/fa6";
import { useNavigate, Link } from "react-router-dom";
import { useDispatch } from 'react-redux';
import AuthHeader from "../../UI/AuthHeader/AuthHeader";
import AuthSideMenu from "../../UI/AuthSideMenu/AuthSideMenu";
import AuthInput from "../../UI/Input/AuthInputs";
import { setFetching, setExamDetails } from "../../../redux/states";
import { checkAuthenticatedUser, formatLargeText, startExamSession } from "../../../utils/client-functions";
import { SaveIconStyled, PreviewCourseModalStyled, CourseVideoRow, SliderBox } from "../../../styled/Resume";
import Slider from '@mui/material/Slider';
import Alert from "@mui/material/Alert";
import Feedback from "../../Dashboard/Feedback";
import { COUNTRIES } from '../../../utils/countries';
import { useTheme } from "../../UI/Theme/ThemeContext";
import useYoutubeCourses from '../../../hooks/useYoutubeCourses';
import { useErrorSetter, useSuccessSetter } from '../../../hooks/useUiMessages';
import OvalSpinner from "../../UI/Spinners/OvalSpinner";
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell 
} from 'recharts';

import {
  Container,
  GlassCard,
  AIHeaderCard,
  ScoreCircleWrapper,
  ScoreText,
  StatGrid,
  StatCard,
  TabNavigation,
  TabItem,
  Badge,
  ProgressBarTrack,
  ProgressBarFill,
  ProgressWrapper,
  ProgressTicker,
  TickHint
} from "../../../styled/InsightNTutor";

import {
  PlainModalOverlay
} from "../../UI/Modal/Modal"
import MainModal from "../../UI/Modal/MainModal";
import Rules from "../Rules";
import useExamStartGate from '../../../hooks/useExamStartGate';
import ExamReadyModal from '../../MockTests/ExamReadyModal';
import { Modal } from '../../UI/Modal/Modal';
import UpgradePopup from "../../UI/SubscriptionPlans/UpgradePopup";


import BubbleFloat from "../../UI/BubbleFloat/BubbleFloat";


const segments = [
    { key: 'grading', label: 'Grading', icon: <FaGraduationCap color="#4A90E2" />, description: "Check out your grades and see which questions you got right or wrong. It’s a great way to celebrate your wins and know exactly what to practice a bit more!" },
    { key: 'studyPlan', label: 'Study Plan', icon: <FaBookOpen color="#00f7ff" />, description: "Here is your very own learning map! It shows you exactly what topics to study each day so you can feel super prepared and confident for your real exam." },
    { key: 'aiTutor', label: 'AI Tutor', icon: <FaRobot color="#A259FF" />, description: "Meet your personal AI tutor! You can ask it to explain anything you find tricky, and it will help you understand it in a simple and fun way." },
    { key: 'examInsights', label: 'Analysis', icon: <FaBolt color="#FFD700" />, description: "Discover secret patterns and helpful tips about your exam. This section shows you what to expect so you can be one step ahead of the rest!" },
    { key: 'peerBenchmarking', label: 'Peer Benchmarking', icon: <FaUsers color="#4A90E2" />, description: "See how your scores compare to other students around the world. It’s like a leaderboard that helps you see where you're doing great!" },
    { key: 'careerAlignment', label: 'Career Match', icon: <FaProjectDiagram color="#E67E22" />, description: "Wondering what you can do when you grow up? This section matches your exam strengths to cool jobs and careers that you might really enjoy!" },
    { key: 'progressMonitoring', label: 'Progress', icon: <FaChartLine color="#A259FF" />, description: "Watch yourself get better every day! These charts show how much you've learned and help you see just how close you are to reaching your big goals." }
];

const examDateOptions = [
    { name: "In 1 week", value: 7 },
    { name: "In 2 weeks", value: 14 },
    { name: "In 1 month", value: 30 },
    { name: "In 2 months", value: 60 },
    { name: "In 3 months", value: 90 },
    { name: "Over 3 months", value: 120 },
];

const isMobile = () => window.innerWidth < 700;


const GradingSegment = ({gradingData, examMeta, themeName}) => {
  const percentage = gradingData.percentage || 0;
  const mainColor = "#43AA8B";
  const scoreColor = gradingData.pass ? mainColor : "#DC143C";
  const mobile = isMobile();
  const [isQuestionExpanded, setIsQuestionExpanded] = useState(false);
  const [isErrorExpanded, setIsErrorExpanded] = useState(false);
  const hasManyQuestions = (gradingData.questionAnalysis?.length || 0) > 3;
  const hasManyErrors = (gradingData.errors?.length || 0) > 3;
  const COLLAPSED_TABLE_MAX_HEIGHT = mobile ? 260 : 280;
  const COLLAPSED_ERRORS_MAX_HEIGHT = mobile ? 210 : 230;

  const renderExpandPill = (expanded, onClick) => (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 14px',
        borderRadius: 999,
        border: '1px solid rgba(95,190,197,0.6)',
        background: themeName === 'dark'
          ? 'linear-gradient(90deg, rgba(95,190,197,0.24), rgba(162,89,255,0.26))'
          : 'linear-gradient(90deg, rgba(95,190,197,0.1), rgba(162,89,255,0.14))',
        color: themeName === 'dark' ? '#e5f9ff' : '#0f3135',
        fontSize: '0.78rem',
        fontWeight: 600,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        cursor: 'pointer',
        boxShadow: '0 8px 20px rgba(95,190,197,0.25)',
        backdropFilter: 'blur(10px)',
        outline: 'none',
        whiteSpace: 'nowrap'
      }}
    >
      <span>See all</span>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 18,
          height: 18,
          borderRadius: '50%',
          background: themeName === 'dark' ? 'rgba(11,34,41,0.9)' : 'rgba(255,255,255,0.9)',
          color: themeName === 'dark' ? '#5Fbec5' : '#3E8F93',
        }}
      >
        {expanded ? <FaChevronUp size={10} /> : <FaChevronDown size={10} />}
      </span>
    </button>
  );
  
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
      <StatGrid>
        <StatCard theme={themeName} iconColor={scoreColor}>
          <div className="icon"><FaTrophy /></div>
          <ScoreCircleWrapper percentage={percentage} color={scoreColor}>
            <svg width="150" height="150">
              <circle className="bg" cx="75" cy="75" r="70" />
              <circle className="progress" cx="75" cy="75" r="70" />
            </svg>
            <ScoreText>
              <span className="value" style={{ color: scoreColor }}>{gradingData.score}</span>
              <span className="label">Total Score</span>
            </ScoreText>
          </ScoreCircleWrapper>
          <div style={{ marginTop: '1rem' }}>
            <Badge variant={gradingData.pass ? 'success' : 'danger'}>
              {gradingData.pass ? 'Passed' : 'Failed'}
            </Badge>
          </div>
        </StatCard>

        <StatCard theme={themeName} iconColor={mainColor}>
          <div className="icon"><FaChartLine /></div>
          <div className="value">{percentage}%</div>
          <div className="label">Accuracy Rate</div>
          <div style={{ marginTop: 'auto', width: '100%' }}>
            <ProgressBarTrack theme={themeName} height="6px">
              <ProgressBarFill progress={percentage} color={mainColor} />
            </ProgressBarTrack>
          </div>
        </StatCard>

        <StatCard theme={themeName} iconColor={mainColor}>
          <div className="icon"><FaHourglass /></div>
          <div className="value">
            {gradingData.questionAnalysis?.reduce((acc, q) => acc + (parseInt(q.time) || 0), 0) || 0}s
          </div>
          <div className="label">Total Time spent</div>
        </StatCard>

        <StatCard theme={themeName} iconColor={mainColor}>
          <div className="icon"><FaUsers /></div>
          <div className="value">
            {percentage > (gradingData.comparative?.average || 0) ? '+' : ''}
            {(percentage - (gradingData.comparative?.average || 0)).toFixed(1)}%
          </div>
          <div className="label">vs. Peer Average</div>
        </StatCard>
      </StatGrid>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem' }}>
        <GlassCard theme={themeName} style={{ flex: '1 1 400px' }}>
          <h6 style={{ marginBottom: '1.5rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FaChartPie color="#43AA8B" /> Topic Performance
          </h6>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem' }}>
            {gradingData.topicBreakdown?.map((t, i) => (
              <div key={i}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ fontSize: '0.9rem', fontWeight: 600 }}>{t.topic}</span>
                  <span style={{ fontSize: '0.9rem', opacity: 0.8 }}>{t.correct}/{t.total} ({Math.round((t.correct/t.total)*100)}%)</span>
                </div>
                <ProgressBarTrack theme={themeName}>
                  <ProgressBarFill progress={(t.correct / t.total) * 100} />
                </ProgressBarTrack>
              </div>
            ))}
          </div>
        </GlassCard>

        <GlassCard theme={themeName} style={{ flex: '1 1 300px' }}>
          <h6 style={{ marginBottom: '1.5rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FaUsers color="#43AA8B" /> Comparative Analysis
          </h6>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem', paddingTop: '1rem' }}>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                <span style={{ fontWeight: 600 }}>Your Performance</span>
                <span style={{ color: '#43AA8B', fontWeight: 700 }}>{percentage}%</span>
              </div>
              <ProgressBarTrack theme={themeName} height="12px">
                <ProgressBarFill progress={percentage} color="#43AA8B" />
              </ProgressBarTrack>
            </div>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                <span style={{ fontWeight: 600 }}>Peer Average</span>
                <span style={{ color: '#FF6B6B', fontWeight: 700 }}>{gradingData.comparative?.average}%</span>
              </div>
              <ProgressBarTrack theme={themeName} height="12px">
                <ProgressBarFill progress={gradingData.comparative?.average} color="#FF6B6B" />
              </ProgressBarTrack>
            </div>
          </div>
        </GlassCard>
      </div>

      <GlassCard theme={themeName}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', marginBottom: '1.5rem' }}>
          <h6 style={{ fontWeight: 700, margin: 0 }}>Question-Level Analysis</h6>
          {hasManyQuestions && renderExpandPill(isQuestionExpanded, () => setIsQuestionExpanded(prev => !prev))}
        </div>
        <div style={{ overflowX: 'auto' }}>
          <div
            style={{
              maxHeight: hasManyQuestions && !isQuestionExpanded ? COLLAPSED_TABLE_MAX_HEIGHT : 'none',
              overflow: hasManyQuestions && !isQuestionExpanded ? 'hidden' : 'visible',
              transition: hasManyQuestions ? 'max-height 0.35s ease' : 'none',
            }}
          >
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${themeName === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}` }}>
                <th style={{ textAlign: 'left', padding: '12px' }}>#</th>
                <th style={{ textAlign: 'left', padding: '12px' }}>Question</th>
                <th style={{ textAlign: 'left', padding: '12px' }}>Result</th>
                <th style={{ textAlign: 'left', padding: '12px' }}>Selected</th>
                <th style={{ textAlign: 'left', padding: '12px' }}>Correct</th>
                <th style={{ textAlign: 'left', padding: '12px' }}>Time</th>
                <th style={{ textAlign: 'left', padding: '12px' }}>Topic</th>
              </tr>
            </thead>
            <tbody>
              {gradingData.questionAnalysis?.map((q, i) => (
                <tr key={i} style={{ borderBottom: `1px solid ${themeName === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)'}` }}>
                  <td style={{ padding: '12px', fontWeight: 700 }}>{q.number}</td>
                  <td style={{ padding: '12px', minWidth: '200px' }}>{q.question || q.question_text}</td>
                  <td style={{ padding: '12px' }}>{q.correct ? <FaCheckCircle color="#43AA8B" size={18} /> : <FaTimesCircle color="#FF6B6B" size={18} />}</td>
                  <td style={{ padding: '12px', color: q.correct ? '#43AA8B' : '#FF6B6B', fontWeight: 600 }}>{q.chose}</td>
                  <td style={{ padding: '12px', color: '#43AA8B', fontWeight: 600 }}>{q.right}</td>
                  <td style={{ padding: '12px' }}>{q.time}s</td>
                  <td style={{ padding: '12px' }}>
                    <Badge variant="success" style={{ background: 'rgba(62, 143, 147, 0.1)', color: '#3E8F93', border: 'none', fontSize: '0.7rem' }}>
                      {q.topic}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      </GlassCard>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', width: '100%' }}>
        <GlassCard theme={themeName}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', marginBottom: '1rem' }}>
            <h6 style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: 0, color: '#FF6B6B' }}>
              <FaRegBookmark /> Error Analysis
            </h6>
            {hasManyErrors && renderExpandPill(isErrorExpanded, () => setIsErrorExpanded(prev => !prev))}
          </div>
          {gradingData.errors?.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '1rem' }}>
              <div style={{ fontSize: '2rem' }}>🎉</div>
              <p>Perfect score! No errors found.</p>
            </div>
          ) : (
            <div
              style={{
                maxHeight: hasManyErrors && !isErrorExpanded ? COLLAPSED_ERRORS_MAX_HEIGHT : 'none',
                overflow: hasManyErrors && !isErrorExpanded ? 'hidden' : 'visible',
                transition: hasManyErrors ? 'max-height 0.35s ease' : 'none',
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {gradingData.errors?.map((e, i) => (
                  <div key={i} style={{ padding: '10px', borderRadius: '12px', background: 'rgba(255, 107, 107, 0.05)', border: '1px solid rgba(255, 107, 107, 0.1)' }}>
                    <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: '4px' }}>Question {e.question}</div>
                    <div style={{ fontSize: '0.85rem', color: themeName === 'dark' ? 'rgba(255,255,255,0.7)' : '#555' }}>{e.mistake}</div>
                    <div style={{ fontSize: '0.8rem', fontStyle: 'italic', marginTop: '4px', color: themeName === 'dark' ? 'rgba(255,255,255,0.5)' : '#777' }}>{e.explanation}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </GlassCard>

        <GlassCard theme={themeName} style={{ borderLeft: '4px solid #A259FF' }}>
          <h6 style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '1rem' }}>
            <FaRobot color="#A259FF" /> Bubble Ai's Feedback Summary
          </h6>
          <p style={{ fontSize: '0.9rem', lineHeight: '1.6', color: themeName === 'dark' ? '#eee' : '#444' }}>
            {gradingData.feedback}
          </p>
        </GlassCard>
      </div>
    </div>
  );
};

const StudyPlanSegment = ({studyPlanData, setStudyPlanData, errorSetter, successSetter, examName, examMetaName, studyPlanTimerElapsed, description, onRequestOpenGrading, themeName, isActuallySubscribed, setIsActuallySubscribed }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { examReady, handleOngoing, handleDoneEvent, confirmStartExam, closeGate } = useExamStartGate(navigate, dispatch);
  const [showRulesModal, setShowRulesModal] = React.useState(false);
  const [selectedMockTask, setSelectedMockTask] = React.useState(null);
  const [selectedVideoTask, setSelectedVideoTask] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [progressStatus, setProgressStatus] = React.useState('Gathering Exam Details...');
  const [progressPercentage, setProgressPercentage] = React.useState(0);
  const [examDate, setExamDate] = useState("");
  const [showPlan, setShowPlan] = useState(false);
  const schedule = (studyPlanData && Array.isArray(studyPlanData.schedule)) ? studyPlanData.schedule : [];
  const [progress, setProgress] = useState(
    schedule.length > 0 ? schedule.map(day => day.tasks.map(task => !!task.done)).flat() : []
  );
  // Notes modal states
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [noteLoading, setNoteLoading] = useState(false);
  const [noteError, setNoteError] = useState(null);
  const [noteContent, setNoteContent] = useState("");
  const [noteTitle, setNoteTitle] = useState("");
  const [noteFullShown, setNoteFullShown] = useState(false);
  const [noteSaved, setNoteSaved] = useState(false);
  const [noteId, setNoteId] = useState(null);
  const [noteSaving, setNoteSaving] = useState(false);
  // Simple in-memory cache for generated notes keyed by a signature per task
  const [noteCache, setNoteCache] = useState({});
  // Track current cache key for the open modal to allow cache updates on unsave/resave
  const [currentCacheKey, setCurrentCacheKey] = useState(null);

  // Video modal & YouTube flow (via shared hook) – fetch 2 results
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [videoQuery, setVideoQuery] = useState("");
  // Spinner should appear immediately when modal opens
  const [initialVideoLoading, setInitialVideoLoading] = useState(false);
  // Mark completion of the first fetch attempt
  const [initialFetchDone, setInitialFetchDone] = useState(false);
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const {
    youtubeVideoId, setYoutubeVideoId,
    youtubeVideos,
    selectedVideoIndex, setSelectedVideoIndex,
    selectedCountry, setSelectedCountry,
    selectedDuration, setSelectedDuration,
    selectedDurationValue, setSelectedDurationValue,
    ytLoading,
    iframeLoading, setIframeLoading,
    savingCourse,
    courseSaved,
    invalidateFetchCache,
    fetchSavedCourses,
    fetchVideos,
    toggleSaveCurrent,
  } = useYoutubeCourses({
    maxResults: 3,
    token,
    product: 'exam',
    onSubscriptionBlocked: () => {
      window.dispatchEvent(new CustomEvent('openExamPricing'));
      errorSetter('Please subscribe to the Exam Simulator to access AI-curated study materials.');
    }
  });

  const durationMarks = [
    { value: 0, label: 'Short' },
    { value: 1, label: 'Medium' },
    { value: 2, label: 'Long' },
  ];

  // Flatten all tasks for table rows
  const allTasks = schedule.flatMap((day, dayIdx) =>
    (Array.isArray(day.tasks) ? day.tasks : []).map((task, taskIdx) => ({
      ...task,
      date: day.date,
      idx: `${dayIdx}-${taskIdx}`
    }))
  );

  // Fetch saved and videos when modal opens or filters change
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!isVideoModalOpen) return;
      await fetchSavedCourses();
      if (cancelled) return;
      await fetchVideos(videoQuery);
      setInitialFetchDone(true);
    })();
    return () => { cancelled = true; };
  }, [isVideoModalOpen, videoQuery, selectedCountry, selectedDuration, fetchSavedCourses, fetchVideos]);

  // Tutorial popover refs & state
  const firstVideoRef = useRef(null);
  const firstNoteRef = useRef(null);
  const firstMockRef = useRef(null);
  const [showVideoTip, setShowVideoTip] = useState(false);
  const [showNoteTip, setShowNoteTip] = useState(false);
  const [showMockTip, setShowMockTip] = useState(false);
  const [tipMeta, setTipMeta] = useState({});
  const [videoInView, setVideoInView] = useState(false);
  const [noteInView, setNoteInView] = useState(false);
  const [mockInView, setMockInView] = useState(false);
  const [videoShownOnce, setVideoShownOnce] = useState(false);
  const [noteShownOnce, setNoteShownOnce] = useState(false);
  const [mockShownOnce, setMockShownOnce] = useState(false);
  const [sequenceCompleted, setSequenceCompleted] = useState(false);
  // Use a shared tip id for all study-plan resource CTAs so "don't show again"
  // affects all relevant popovers in this segment.
  const STUDYPLAN_TIP_ID = 'insightn:studyplan:resource-cta';

  // determine first video/note indexes among tasks
  const firstVideoIdx = allTasks.findIndex(t => {
    const r = t.resource || {};
    const rType = (typeof r === 'string' ? '' : (r.resourceType || r.type || '')).toLowerCase();
    return rType.includes('video');
  });
  const firstNoteIdx = allTasks.findIndex(t => {
    const r = t.resource || {};
    const rType = (typeof r === 'string' ? '' : (r.resourceType || r.type || '')).toLowerCase();
    return rType.includes('note');
  });
  const firstMockIdx = allTasks.findIndex(t => {
    const r = t.resource || {};
    const rType = (typeof r === 'string' ? '' : (r.resourceType || r.type || '')).toLowerCase();
    return rType.includes('mock');
  });

  // Load server-side tip meta when plan is shown
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!showPlan) return;
      try {
        const v = await getTutorialTipMeta(STUDYPLAN_TIP_ID);
        if (!mounted) return;
        setTipMeta(v || {});
      } catch (e) {}
    })();
    return () => { mounted = false; };
  }, [showPlan]);

  

  // Intersection observers for in-view detection
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;
    const obs = new IntersectionObserver(entries => {
      entries.forEach(ent => {
        if (ent.target === firstVideoRef.current) setVideoInView(ent.isIntersecting);
        if (ent.target === firstNoteRef.current) setNoteInView(ent.isIntersecting);
        if (ent.target === firstMockRef.current) setMockInView(ent.isIntersecting);
      });
    }, { threshold: 0.5 });
    if (firstVideoRef.current) obs.observe(firstVideoRef.current);
    if (firstNoteRef.current) obs.observe(firstNoteRef.current);
    if (firstMockRef.current) obs.observe(firstMockRef.current);
    return () => obs.disconnect();
  }, [showPlan]);

  // Show video tip when its button becomes visible and user hasn't opted out
  useEffect(() => {
    if (videoInView && showPlan && studyPlanTimerElapsed && !videoShownOnce && !sequenceCompleted) {
      if (!tipMeta?.dontShow) {
        setShowVideoTip(true);
        setVideoShownOnce(true);
      }
    }
  }, [videoInView, tipMeta, showPlan, studyPlanTimerElapsed, videoShownOnce, sequenceCompleted]);
  // Note and mock tips are shown in sequence after the previous tip is closed.
  // (Do not auto-open them simultaneously.)

  // Handlers to record and sequence tips
  const handleCloseVideoTip = async () => {
    // Close current video tip. Do NOT persist any "dontShow"
    // closing with X behaves as a temporary dismiss only. Sequence to next tip after delay.
    setShowVideoTip(false);
    setTimeout(async () => {
      try {
        const n = await getTutorialTipMeta(STUDYPLAN_TIP_ID);
        if (!sequenceCompleted && firstNoteRef.current && noteInView && !noteShownOnce && !(n && n.dontShow)) {
          setShowNoteTip(true);
          setNoteShownOnce(true);
        }
      } catch (e) {}
    }, 6000);
  };

  const handleCloseNoteTip = async () => {
    setShowNoteTip(false);
    setTimeout(async () => {
      try {
        const n = await getTutorialTipMeta(STUDYPLAN_TIP_ID);
        if (!sequenceCompleted && firstMockRef.current && mockInView && !mockShownOnce && !(n && n.dontShow)) {
          setShowMockTip(true);
          setMockShownOnce(true);
        }
      } catch (e) {}
    }, 6000);
  };

  const handleCloseMockTip = async () => {
    // Close the final tip and mark the sequence completed so none re-open.
    setShowMockTip(false);
    setSequenceCompleted(true);
  };

  // Clear the initial spinner once a video is selected or after first fetch completes and loading ends
  useEffect(() => {
    if (initialVideoLoading && (youtubeVideoId || (initialFetchDone && !ytLoading && !iframeLoading))) {
      setInitialVideoLoading(false);
    }
  }, [initialVideoLoading, youtubeVideoId, ytLoading, iframeLoading, initialFetchDone]);

  const handleTick = async idx => {
    setProgress(prev => {
      const newProgress = [...prev];
      newProgress[idx] = !newProgress[idx];
      return newProgress;
    });

    // Find the task info for backend update
    const task = allTasks[idx];
    if (!task) return;

    try {
      await axios.post(
        "/mock/update-studyplan-progress",
        {
          examId: new URLSearchParams(window.location.search).get("examId"),
          date: task.date,
          taskIdx: idx,
          done: !progress[idx]
        },
        {
          headers: { "x-access-token": localStorage.getItem("token") }
        }
      );

      successSetter("Progress updated!");
    } catch (err) {
      errorSetter(err?.response?.data?.message || err?.response?.data?.error || "Failed to update progress. Please try again.");
    }
  };

  // Handle generating & showing study note for a task resource of type "note"
  const handleNoteClick = async (task) => {
    try {
      const resource = task.resource || {};
      // Derive a human friendly title
      const derivedTitle = resource.title || task.title || "Study Note";
      setNoteTitle(derivedTitle);
      setShowNoteModal(true);
      setNoteLoading(true);
      setNoteError(null);
      setNoteContent("");
      setNoteFullShown(false);
      setNoteId(null);
      // Gather context
      const examId = new URLSearchParams(window.location.search).get("examId");
      // Cache key: examId + title + prompt
      const cacheKey = `${examId}::${resource.title || derivedTitle}::${resource.prompt || ''}`;
      setCurrentCacheKey(cacheKey);
      // If cached, use it and skip network
      if (noteCache[cacheKey]) {
        const cached = noteCache[cacheKey];
        setNoteContent(cached.note);
        setNoteSaved(true);
        setNoteId(cached.noteId ? String(cached.noteId) : null);
        setNoteLoading(false);
        return;
      }
      // Backend endpoint (to be implemented). We optimistically call; if 404 we soft-fail.
      const payload = {
        examId,
        resource: {
          type: resource.type || resource.resourceType || "Notes",
          title: resource.title || derivedTitle,
          prompt: resource.prompt || `Generate comprehensive notes for ${derivedTitle}`
        }
      };
      try {
        const resp = await axios.post('/mock/generate-studyplan-note', payload, { headers: { 'x-access-token': localStorage.getItem('token') } });
        const note = resp?.data?.note || resp?.data?.content || resp?.data?.text || "";
        if (!note || note.length < 40) {
          throw new Error('Empty note returned');
        }
        setNoteContent(note);
        setNoteSaved(true); // auto-saved backend
        const nid = resp?.data?.noteId ? String(resp.data.noteId) : null;
        setNoteId(nid);
        setNoteCache(prev => ({ ...prev, [cacheKey]: { note, noteId: nid } }));
        successSetter('Note generated and saved');
      } catch (e) {
        if (e?.response?.data?.openPricing) {
          window.dispatchEvent(new CustomEvent('openExamPricing'));
        }
        console.warn('Note generation failed or endpoint missing', e?.message);
        setNoteError(e?.response?.data?.message || e?.response?.data?.error || 'Failed to generate note.');
      }
    } catch (err) {
      setNoteError('Unexpected error preparing note.');
    } finally {
      setNoteLoading(false);
    }
  };

  const truncatedNote = (() => {
    if (!noteContent) return "";
    const words = noteContent.trim().split(/\s+/);
    if (words.length <= 300) return noteContent;
    return words.slice(0, 300).join(' ') + '...';
  })();
  const isNoteTruncated = (() => {
    if (!noteContent) return false;
    const words = noteContent.trim().split(/\s+/);
    return words.length > 300;
  })();

  const handleSaveNote = async () => {
    if (noteSaving) return; // prevent double clicks
    // If saved -> unsave; if unsaved and we still have content -> re-save
    if (noteSaved) {
      if (noteId) {
        try {
          setNoteSaving(true);
          await axios.delete(`/mock/studyplan-notes/${noteId}`, { headers: { 'x-access-token': localStorage.getItem('token') } });
          setNoteSaved(false);
          // Clear cached noteId so subsequent actions reflect unsaved state
          if (currentCacheKey) {
            setNoteCache(prev => ({ ...prev, [currentCacheKey]: { ...(prev[currentCacheKey] || {}), noteId: null } }));
          }
          successSetter('Note unsaved');
        } catch (e) {
          setNoteSaved(true);
          return errorSetter(e?.response?.data?.message || e?.response?.data?.error || 'Could not unsave note.');
        } finally {
          setNoteSaving(false);
        }
      }
    } else {
      // Re-save
      if (noteContent) {
        const examId = new URLSearchParams(window.location.search).get('examId');
        try {
          setNoteSaving(true);
          const resp = await axios.post('/mock/studyplan-notes/resave', {
            examId,
            title: noteTitle || 'Study Note',
            prompt: '',
            note: noteContent
          }, { headers: { 'x-access-token': localStorage.getItem('token') } });
          setNoteSaved(true);
          const nid = resp?.data?.noteId ? String(resp.data.noteId) : null;
          setNoteId(nid);
          if (currentCacheKey) {
            setNoteCache(prev => ({ ...prev, [currentCacheKey]: { ...(prev[currentCacheKey] || {}), noteId: nid } }));
          }
          successSetter('Note re-saved');
        } catch (e) {
          errorSetter(e?.response?.data?.message || e?.response?.data?.error || 'Could not re-save note.');
        } finally {
          setNoteSaving(false);
        }
      } else {
        errorSetter('No note content to save.');
      }
    }
  };

  useEffect(() => {
    // Only show the plan if studyPlanData has a non-empty schedule array
    if (
      studyPlanData &&
      Array.isArray(studyPlanData.schedule) &&
      studyPlanData.schedule.length > 0
    ) {
      setShowPlan(true);
    } else {
      setShowPlan(false);
    }
  }, [studyPlanData]);

  const handleGetStudyPlan = useCallback(async () => {
    try {
      if (!examDate) {
        errorSetter("Please select your exam date before generating a study plan.");
        return;
      }

      const today = new Date();
      const currentDate = today.toISOString().split("T")[0];
      const urlParams = new URLSearchParams(window.location.search);
      const examId = urlParams.get("examId");

      if (!examId) {
        errorSetter("No exam ID found. Please return to your dashboard and try again.");
        return;
      }

      setLoading(true);
      setProgressStatus("Starting your study plan generation...");
      setProgressPercentage(Math.random() * 10); // initial progress based on time until exam

      const response = await axios.post(
        "/mock/get-study-plan",
        { examId, examDate, currentDate },
        { headers: { "x-access-token": localStorage.getItem("token") } }
      );

      if (response.data && response.data.studyPlan) {
        setShowPlan(true);
        setStudyPlanData(response.data.studyPlan);
        if (typeof response.data.isSubscribed !== 'undefined') {
          setIsActuallySubscribed(!!response.data.isSubscribed);
        }
        successSetter("Study plan retrieved successfully!");
        setLoading(false);
        return;
      }

      const jobId = response.data?.jobId;
      if (!jobId) {
        throw new Error("Could not initialize study plan generation.");
      }

      const eventSource = new EventSource(`/mock/study-plan-progress/${jobId}`);

      eventSource.onmessage = (event) => {
        const data = JSON.parse(event.data);

        if (data.status === 'processing') {
          setProgressStatus(data.message || "Generating your study plan...");
          setProgressPercentage(data.percent || 0);
        } else if (data.status === 'completed') {
          eventSource.close();
          setShowPlan(true);
          setStudyPlanData(data.studyPlan);
          successSetter("Study plan generated successfully!");
          setLoading(false);
        } else if (data.status === 'failed') {
          eventSource.close();
          errorSetter(data.error || "Failed to generate study plan.");
          setLoading(false);
        }
      };

      eventSource.onerror = () => {
        eventSource.close();
        errorSetter("Lost connection to generation server. Please try again.");
        setLoading(false);
      };

    } catch (err) {
      setLoading(false);
      if (err?.response?.data?.openPricing) {
        window.dispatchEvent(new CustomEvent('openExamPricing'));
      }
      errorSetter(
        err?.response?.data?.message || err?.response?.data?.error ||
        "Failed to generate study plan. Please try again."
      );
    }
  }, [examDate, errorSetter, successSetter, setStudyPlanData, setIsActuallySubscribed]);

  // Assemble payload for starting an exam from a study-plan task
  const assemblePayloadFromTask = (task) => {
    const resource = task?.resource || {};
    const isStringResource = typeof resource === 'string';
    const resObj = isStringResource ? {} : resource;

    const examParamName = examMetaName || examName || '';
    const subject = resObj.subject || resObj.title || task.title || examParamName;

    // prefer explicit seconds; fall back to minutes->seconds
    let timeAlloted = resObj.timeAlloted || resObj.timeAllotted || 0;
    if (!timeAlloted || typeof timeAlloted !== 'number' || timeAlloted <= 0) {
      const mins = Number(task.timeAllocation) || 45;
      timeAlloted = mins * 60;
    }

    const description = resObj.description || resObj.prompt || task.title || '';
    const level = resObj.level || resObj.levelName || '';

    const payload = {
      examName: examParamName,
      subject,
      timeAlloted,
      description,
      level,
    };

    // optional fields
    if (resObj.totalQuestions) payload.totalQuestions = resObj.totalQuestions;
    if (resObj.passMarkPercent) payload.passMarkPercent = resObj.passMarkPercent;

    return payload;
  };

  const handleStartExamFromPlan = async () => {
    // Ensure a task is selected
    if (!selectedMockTask) return;
    try {
      await checkAuthenticatedUser();
    } catch (err) {
      return navigate('/popin?mock');
    }

    const payload = assemblePayloadFromTask(selectedMockTask);

    setLoading(true);
    setProgressStatus('Gathering Exam Details...');
    setProgressPercentage(5);

    const token = localStorage?.getItem('token');
    try {
      await startExamSession({
        payload,
        tokenOverride: token,
        onOngoing: (examDetails) => { setLoading(false); handleOngoing(examDetails); },
        onDone: (e) => { handleDoneEvent(e); },
        onError: (msg) => { setLoading(false); errorSetter(msg); },
        onJobCreated: (jobId) => { setShowRulesModal(false); },
        setLoading, setProgressStatus, setProgressPercentage
      });
    } catch (e) {
      console.error('[InsightNTutor] start-exam POST failed', e);
      setLoading(false);
      errorSetter(e?.response?.data?.message || e?.response?.data?.error || 'An Error Occurred. Please try again. If issue persists, please chat with us.');
      if (e?.response?.data?.openPricing) {
        window.dispatchEvent(new CustomEvent('openExamPricing'));
      }
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {loading && (
        <PlainModalOverlay theme={themeName} style={{ zIndex: 10000 }}>
          <div style={{ textAlign: 'center', background: themeName === 'dark' ? '#1a1a1a' : '#fff', padding: '3rem', borderRadius: '24px', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
            <Vortex
              visible={true}
              height={120}
              width={120}
              ariaLabel="vortex-loading"
              colors={["#3E8F93", "#0d3d40", "#3E8F93", "#0d3d40", "#3E8F93", "#0d3d40"]}
            />
            <h4 style={{ color: themeName === 'dark' ? '#fff' : '#000', fontWeight: 800, marginTop: '20px' }}>{progressStatus}</h4>
            <div style={{ width: '300px', height: '10px', background: themeName === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)', borderRadius: '10px', margin: '20px auto', overflow: 'hidden' }}>
              <div style={{ width: `${progressPercentage}%`, height: '100%', background: '#3E8F93', transition: 'width 0.3s ease' }} />
            </div>
            <p style={{ color: themeName === 'dark' ? 'rgba(255,255,255,0.6)' : '#666', fontSize: '0.9rem' }}>{progressPercentage}% Completed</p>
          </div>
        </PlainModalOverlay>
      )}
      <div style={{ marginTop: '1rem' }}>
        {showPlan && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h6 style={{ fontWeight: 800, fontSize: '1.2rem', margin: 0, color: themeName === 'dark' ? '#fff' : '#000' }}>Integrated Study Roadmap</h6>
              <div style={{ opacity: 0.6, fontSize: '0.85rem', color: themeName === 'dark' ? '#fff' : '#000' }}>
                Progress: {Math.round((progress.filter(p => p).length / progress.length) * 100)}%
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {allTasks.map((task, i) => {
                const isHighPriority = task.importance >= 4;
                return (
                  <GlassCard key={task.idx} theme={themeName} style={{ borderLeft: `6px solid ${isHighPriority ? '#FF6B6B' : '#3E8F93'}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: '250px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                          <h6 style={{ margin: 0, fontWeight: 800, fontSize: '1.05rem' }}>{task.title}</h6>
                          {isHighPriority && <Badge variant="danger">High Priority</Badge>}
                        </div>
                        <div style={{ display: 'flex', gap: '1.5rem', color: themeName === 'dark' ? 'rgba(255,255,255,0.6)' : '#666', fontSize: '0.85rem' }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <FaCalendarAlt size={12} color="#3E8F93" /> {task.date}
                          </span>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <FaHourglass size={12} color="#3E8F93" /> {task.timeAllocation}m
                          </span>
                        </div>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', marginLeft: 'auto' }}>
                        {task.resource && (
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            {(() => {
                              const isStringResource = typeof task.resource === 'string';
                              const rType = (isStringResource ? '' : (task.resource.resourceType || task.resource.type || '')).toLowerCase();
                              const baseType = rType.includes('mock') ? 'mock' : rType.includes('note') ? 'note' : rType.includes('video') ? 'video' : 'note';
                              const ctaLabel = baseType === 'mock' ? 'Take Mock' : baseType === 'video' ? 'Watch Videos' : 'AI Notes';
                              
                              let icon = <FaBookOpen />;
                              if (baseType === 'mock') icon = <FaTrophy />;
                              if (baseType === 'video') icon = <FaVideo />;

                              const handleClick = async () => {
                                if (!isActuallySubscribed) {
                                  window.dispatchEvent(new CustomEvent('openExamPricing'));
                                  return;
                                }
                                if (rType.includes('note')) {
                                  handleNoteClick(task);
                                } else {
                                  if (rType.includes('mock')) {
                                    // Assemble a lightweight payload and open Rules modal for confirmation
                                    setSelectedMockTask(task);
                                    setShowRulesModal(true);
                                  } else if (rType.includes('video')) {
                                    // Open video modal and derive query similar to PreviewResume
                                    const res = task.resource || {};
                                    const derived = res.title || task.title || `${examName || ''} ${task.title || ''}`;
                                    // Ensure we force a fresh fetch for an explicit user action
                                    setSelectedVideoTask(task);
                                    setVideoQuery(derived);
                                    invalidateFetchCache();
                                    setInitialVideoLoading(true);
                                    // Force a fresh fetch (bypass cache) so we do not reuse previous videos
                                    try { fetchVideos && fetchVideos(derived, true); } catch (_) {}
                                    setIsVideoModalOpen(true);
                                  } else {
                                    // Unknown resource type: treat as note rather than showing coming soon
                                    handleNoteClick(task);
                                  }
                                }
                              };

                              const isDone = progress[i];
                              const isMock = baseType === 'mock';
                              const finalCtaLabel = isDone ? 'Review' : ctaLabel;

                              const btnRef = (i === firstVideoIdx && baseType === 'video') ? firstVideoRef :
                                            (i === firstNoteIdx && baseType === 'note') ? firstNoteRef :
                                            (i === firstMockIdx && baseType === 'mock') ? firstMockRef : undefined;

                              const btnStyle = { 
                                padding: '0 20px', 
                                borderRadius: '12px', 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center', 
                                gap: '10px', 
                                fontSize: '0.85rem', 
                                height: '37px', 
                                width: 'fit-content', 
                                minWidth: '140px',
                                color: '#ffffff'
                              };

                              if (isDone) {
                                return (
                                  <ButtonThin
                                    onClick={isMock ? undefined : handleClick}
                                    ref={btnRef}
                                    style={{ 
                                      ...btnStyle, 
                                      opacity: isMock ? 0.6 : 1, 
                                      cursor: isMock ? 'not-allowed' : 'pointer',
                                      backgroundColor: themeName === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#F3F0F1',
                                      border: themeName === 'dark' ? '1px solid rgba(255, 255, 255, 0.2)' : 'none'
                                    }}
                                  >
                                    {icon} {finalCtaLabel}
                                  </ButtonThin>
                                );
                              }

                              return (
                                <ButtonSubmitGreen
                                  onClick={handleClick}
                                  ref={btnRef}
                                  style={btnStyle}
                                >
                                  {icon} {ctaLabel}
                                </ButtonSubmitGreen>
                              );
                            })()}
                          </div>
                        )}
                        <ProgressWrapper>
                          {!progress[i] && <TickHint>Mark as Done</TickHint>}
                          <ProgressTicker 
                            theme={themeName}
                            isDone={progress[i]}
                            onClick={() => handleTick(i)}
                          >
                            {progress[i] && <FaCheckCircle size={16} />}
                          </ProgressTicker>
                        </ProgressWrapper>
                      </div>
                    </div>
                  </GlassCard>
                );
              })}
            </div>

            <GlassCard theme={themeName}>
              <h6 style={{ marginBottom: '1.5rem', fontWeight: 700 }}>Time Allocation Guide (% of Minutes)</h6>
              <div style={{ width: '100%', height: '350px' }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={studyPlanData.timeAllocation}
                      dataKey="percent"
                      nameKey="topic"
                      cx="50%"
                      cy="50%"
                      innerRadius={70}
                      outerRadius={100}
                      paddingAngle={5}
                      label={({ name }) => name}
                    >
                      {studyPlanData.timeAllocation?.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={['#3E8F93', '#6FCBD1', '#FF6B6B', '#F7B801', '#6C63FF'][index % 5]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ 
                      borderRadius: '12px', 
                      border: 'none', 
                      boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                      background: themeName === 'dark' ? '#1a1a1a' : '#fff',
                      color: themeName === 'dark' ? '#fff' : '#000'
                    }} itemStyle={{ color: themeName === 'dark' ? '#fff' : '#000' }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </GlassCard>
          </div>
        )}
      </div>
      {!showPlan && (
        <MainModal isOpen={!showPlan} onClose={() => { setShowPlan(true); if (typeof onRequestOpenGrading === 'function') onRequestOpenGrading(); }} width="650px">
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '3.5rem', marginBottom: '1.5rem' }}>🎯</div>
            <h3 style={{ fontWeight: 800, marginBottom: '1rem', color: '#111' }}>Personalized Study Plan</h3>
            <p style={{ lineHeight: 1.6, marginBottom: '2rem', color: '#555' }}>
              I'll analyze your recent performance to build a custom roadmap to help you focus instead of having to read everything. Select your target exam date to generate a plan that optimizes your remaining study time.
            </p>
            
            <div style={{ maxWidth: '400px', margin: '0 auto' }}>
              <div style={{ textAlign: 'left', marginBottom: '1.5rem' }}>
                <span style={{ fontSize: '0.85rem', fontWeight: 700, display: 'block', marginBottom: '8px', color: '#111' }}>TARGET EXAM DATE</span>
                <select 
                  value={examDate} 
                  onChange={e => setExamDate(e.target.value)}
                  style={{ 
                    width: '100%', 
                    padding: '12px 16px', 
                    borderRadius: '12px', 
                    background: themeName === 'dark' ? '#2c2c2c' : 'rgba(0,0,0,0.05)', 
                    border: '1px solid rgba(0,0,0,0.1)', 
                    outline: 'none',
                    color: themeName === 'dark' ? '#fff' : '#000'
                  }}
                >
                  <option value="" style={{ background: themeName === 'dark' ? '#2c2c2c' : '#fff', color: themeName === 'dark' ? '#fff' : '#000' }}>Select Target Date...</option>
                  {examDateOptions.map(opt => (
                    <option key={opt.value} value={opt.value} style={{ background: themeName === 'dark' ? '#2c2c2c' : '#fff', color: themeName === 'dark' ? '#fff' : '#000' }}>{opt.name}</option>
                  ))}
                </select>
              </div>
              
              <ButtonSubmitGreen 
                disabled={!examDate} 
                onClick={handleGetStudyPlan}
                style={{ width: '100%', borderRadius: '30px', padding: '15px' }}
              >
                <FaCalendarAlt style={{ marginRight: 8 }} /> Generate Study Plan
              </ButtonSubmitGreen>
            </div>
          </div>
        </MainModal>
      )}
      {showNoteModal && (
        <MainModal isOpen={showNoteModal} onClose={() => setShowNoteModal(false)} width={"55%"} height={600}>
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <div style={{ padding: '2rem', flex: 1, overflowY: 'auto' }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:12, marginBottom: '2rem' }}>
                <h3 style={{ margin:0, color: '#3E8F93', fontWeight: 800 }}>{noteTitle || 'Study Note'}</h3>
              </div>
              {noteLoading && (
                <div style={{display:'flex', flexDirection:'column', alignItems:'center', padding:'30px 0'}}>
                  <Vortex
                    visible={true}
                    height={100}
                    width={100}
                    ariaLabel="note-loading"
                    colors={["#3E8F93", "#0d3d40", "#3E8F93", "#0d3d40", "#3E8F93", "#0d3d40"]}
                  />
                  <p style={{marginTop:20, fontSize:'0.9rem', textAlign:'center', color: '#666'}}>Generating your study notes for {noteTitle + "..." || 'Study Note'}</p>
                </div>
              )}
              {!noteLoading && noteError && (
                <div style={{color:'#d80707', marginTop:12, textAlign: 'center'}}>{noteError}</div>
              )}
              {!noteLoading && !noteError && noteContent && (
                <div style={{ fontSize:'1rem', lineHeight: '1.6', color: '#111' }}>
                  {formatLargeText(noteFullShown ? noteContent : truncatedNote)}
                </div>
              )}
            </div>
            
            <div style={{ borderTop:'1px solid rgba(0,0,0,0.05)', padding:'1.5rem 2rem', display:'flex', alignItems:'center', justifyContent:'space-between', background: 'rgba(0,0,0,0.02)' }}>
              <div>
                {isNoteTruncated && (
                  <ButtonThin onClick={() => setNoteFullShown(prev => !prev)} style={{ border: 'none', background: 'transparent', fontWeight: 700 }}>
                    {noteFullShown ? 'SHOW LESS' : 'EXPAND FULL NOTE'}
                  </ButtonThin>
                )}
              </div>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                <SaveIconStyled onClick={handleSaveNote} title={noteSaved ? 'Unsave note' : 'Save note'}>
                  {noteSaving ? (
                    <Oval height={20} width={20} color="#3E8F93" strokeWidth={4} />
                  ) : noteSaved ? (
                    <FaBookmark color="#3E8F93" size={24} />
                  ) : (
                    <FaRegBookmark color="#3E8F93" size={24} />
                  )}
                </SaveIconStyled>
              </div>
            </div>
          </div>
        </MainModal>
      )}
      {isVideoModalOpen && (
        <MainModal isOpen={isVideoModalOpen} onClose={() => { setIsVideoModalOpen(false); setSelectedVideoTask(null); }} width={"50%"} height={520}>
          <div>
            <PreviewCourseModalStyled>
              <h3 style={{ color: themeName === 'dark' ? '#fff' : '#000' }}>{videoQuery || "Recommended Materials"}</h3>
              <p style={{ color: themeName === 'dark' ? 'rgba(255,255,255,0.7)' : '#666' }}>{(youtubeVideos?.[selectedVideoIndex]?.description) || (selectedVideoTask?.resource?.description) || ""}</p>
              
              <CourseVideoRow>
                <div className="videoWrapper">
                  {(ytLoading || iframeLoading || initialVideoLoading) && (
                    <div style={{position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center', background: themeName === 'dark' ? '#122732' : '#efefef'}}>
                      <OvalSpinner size={36} color="#5fbec5" />
                    </div>
                  )}
                  {(() => {
                    const embedFromYouTube = youtubeVideoId ? `https://www.youtube.com/embed/${youtubeVideoId}` : null;
                    const legacySrc = (!embedFromYouTube && initialFetchDone && !(ytLoading || iframeLoading || initialVideoLoading))
                      ? (selectedVideoTask?.resource?.videoSource || selectedVideoTask?.resource?.link || selectedVideoTask?.resource?.url || null)
                      : null;

                    if (embedFromYouTube) {
                      return (
                        <iframe
                          src={embedFromYouTube}
                          title="Course video"
                          style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", border: 0 }}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          onLoad={() => setIframeLoading(false)}
                        />
                      );
                    }
                    if (legacySrc) {
                      return (
                        <iframe
                          src={legacySrc}
                          title="Course video"
                          style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", border: 0 }}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          onLoad={() => setIframeLoading(false)}
                        />
                      );
                    }
                    if (initialFetchDone && !(ytLoading || iframeLoading || initialVideoLoading)) {
                      return (
                        <div style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", background: themeName === 'dark' ? '#122732' : "#f6f6f6", color: themeName === 'dark' ? "#fff" : "#444" }}>
                          <span>No preview available</span>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              </CourseVideoRow>

              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 12, gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <SliderBox>
                  <Slider
                    value={selectedDurationValue}
                    onChange={(e, val) => {
                      setSelectedDurationValue(val);
                      setSelectedDuration(val === 0 ? 'short' : val === 1 ? 'medium' : 'long');
                    }}
                    onChangeCommitted={() => { invalidateFetchCache(); fetchVideos(videoQuery); }}
                    step={1}
                    marks={durationMarks}
                    min={0}
                    max={2}
                    aria-label="video-duration"
                    sx={{
                      color: '#5fbec5',
                      height: 3,
                      padding: '2px 0',
                      '& .MuiSlider-rail': { opacity: 0.3, backgroundColor: 'rgba(95,190,197,0.30)' },
                      '& .MuiSlider-track': { border: 'none', backgroundColor: '#5fbec5' },
                      '& .MuiSlider-thumb': {
                        height: 12,
                        width: 12,
                        backgroundColor: '#fff',
                        border: '2px solid #5fbec5',
                        boxShadow: 'none',
                        '&:hover, &.Mui-focusVisible, &.Mui-active': { boxShadow: '0 0 0 6px rgba(95,190,197,0.16)' }
                      },
                      '& .MuiSlider-markLabel, & .MuiSlider-markLabelActive': { fontSize: '0.7rem', color: themeName === 'dark' ? '#fff' : 'inherit' }
                    }}
                  />
                </SliderBox>

                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                  <select
                    value={selectedCountry}
                    onChange={(e) => { setSelectedCountry(e.target.value); invalidateFetchCache(); fetchVideos(videoQuery); }}
                    style={{
                      height: 34,
                      padding: '4px 8px',
                      borderRadius: 9,
                      background: themeName === 'dark' ? '#1a1a1a' : '#fff',
                      color: themeName === 'dark' ? '#fff' : '#111',
                      border: '1px solid rgba(0,0,0,0.12)',
                      width: (typeof window !== 'undefined' ? ((window.innerWidth||0) < 600) : false) ? '120px' : '200px',
                      minWidth: 120,
                      maxWidth: 200,
                      fontSize: '0.85rem'
                    }}
                    title="Filter by country"
                  >
                    <option value="">Choose region</option>
                    {COUNTRIES && COUNTRIES.map((c, idx) => (
                      <option key={`${c.code}-${idx}`} value={c.code} style={{ background: themeName === 'dark' ? '#1a1a1a' : '#fff', color: themeName === 'dark' ? '#fff' : '#111' }}>{c.name} ({c.code})</option>
                    ))}
                  </select>

                  <SaveIconStyled
                    className={courseSaved ? 'saved' : undefined}
                    saved={courseSaved}
                    title={courseSaved ? "Unsave" : "Save Material"}
                    onClick={async () => {
                      const res = await toggleSaveCurrent({
                        title: youtubeVideos?.[selectedVideoIndex]?.title || videoQuery || 'Study Material',
                        description: youtubeVideos?.[selectedVideoIndex]?.description || '',
                        thumbnail: youtubeVideos?.[selectedVideoIndex]?.thumbnail || '',
                        sourceQuery: videoQuery,
                      });
                      if (res.ok) {
                        if (res.action === 'saved') successSetter('Material saved');
                        if (res.action === 'unsaved') successSetter('Removed from saved');
                      } else {
                        errorSetter('Could not update saved material. Please try again.');
                      }
                    }}
                  >
                    {savingCourse ? (
                      <OvalSpinner size={18} color="#5fbec5" />
                    ) : courseSaved ? (
                      <FaBookmark color="#5fbec5" />
                    ) : (
                      <FaRegBookmark color="#5fbec5" />
                    )}
                  </SaveIconStyled>
                </div>
              </div>

              <Alert severity="info" sx={{ mt: 1, fontSize: '.8rem', '& .MuiAlert-message': { width: '100%' } }}>
                Use the range to pick course length (Short/Medium/Long), then optionally filter by country to localize results. Click a thumbnail to switch the main video, and tap the bookmark to save material for later.
              </Alert>
              <Alert severity="info" sx={{ mt: 1, fontSize: '.8rem', '& .MuiAlert-message': { width: '100%' } }}>
                To view saved materials from anywhere: <strong>Top right menu → <Link to="/user/dashboard/courses" style={{ color: '#5fbec5', textDecoration: 'underline' }}>Ai Curated Courses</Link>.</strong>
              </Alert>

              {Array.isArray(youtubeVideos) && youtubeVideos.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <h4 style={{ margin: '8px 0', color: themeName === 'dark' ? '#fff' : '#000' }}>Free Videos</h4>
                  <div className="videoChoices">
                    {youtubeVideos.map((v, idx) => (
                      <div key={v.videoId || v.title || idx} style={{ display: 'inline-block', marginRight: 8 }}>
                        <img
                          src={v.thumbnail || ''}
                          alt={v.title || `video-${idx}`}
                          className={`thumb ${selectedVideoIndex === idx ? 'selected' : ''}`}
                          onClick={() => {
                            if (v && v.videoId) {
                              setYoutubeVideoId(v.videoId);
                              setSelectedVideoIndex(idx);
                            }
                          }}
                          style={{ display: 'block' }}
                        />
                        <div style={{ maxWidth: 140, fontSize: 12, marginTop: 6, color: themeName === 'dark' ? '#fff' : '#444', lineHeight: '1.2' }}>{v.title}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {ytLoading && (
                <div style={{ marginTop: 12, width:'100%' }}>
                  <h4 style={{ margin: '8px 0', color: themeName === 'dark' ? '#fff' : '#000' }}>Relevant Materials</h4>
                  <div className="videoChoices">
                    {[0,1,2].map((i) => (
                      <div key={i} style={{ display:'inline-block', marginRight:8 }}>
                        <div className="thumbLoader" />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </PreviewCourseModalStyled>
          </div>
        </MainModal>
      )}

      {showVideoTip && (
        <TutorialPopover
          show={showVideoTip}
          tipId={STUDYPLAN_TIP_ID}
          title="Aggregate Videos"
          description="Recommended: I will aggregate the most relevant study videos to help you learn easily and quickly. Try it to open recommended video material(s)."
          anchorRef={firstVideoRef}
          direction="bottom"
          theme="auto"
          onClose={handleCloseVideoTip}
          onPrimary={() => { firstVideoRef.current && firstVideoRef.current.click(); }}
        />
      )}
      {showNoteTip && (
        <TutorialPopover
          show={showNoteTip}
          tipId={STUDYPLAN_TIP_ID}
          title="Generate Note"
          description="Generate concise study notes for this task. Tap Try it and I'll create the note based on identified weak points."
          anchorRef={firstNoteRef}
          direction="bottom"
          theme="auto"
          onClose={handleCloseNoteTip}
          onPrimary={() => { firstNoteRef.current && firstNoteRef.current.click(); }}
        />
      )}
      {showMockTip && (
        <TutorialPopover
          show={showMockTip}
          tipId={STUDYPLAN_TIP_ID}
          title="Take Mock"
          description="After completing the above study tasks, assess yourself again. Generate a timed mock. Tap Try it to begin."
          anchorRef={firstMockRef}
          direction="bottom"
          theme="auto"
          onClose={handleCloseMockTip}
          onPrimary={() => { firstMockRef.current && firstMockRef.current.click(); }}
        />
      )}
      {showRulesModal && selectedMockTask && (
        <Rules
          examName={examMetaName || examName}
          subject={selectedMockTask.resource || { subject: selectedMockTask.title, timeAlloted: (selectedMockTask.timeAllocation || 45) * 60 || 2700, description: selectedMockTask.title }}
          closeModalsAndReset={() => { setShowRulesModal(false); setSelectedMockTask(null); }}
          handleStartExam={handleStartExamFromPlan}
          description={description}
        />
      )}

      {loading && (
        <Modal
          header4={`PROCESSING... DO NOT RELOAD`}
          header3={progressStatus}
          progress={progressPercentage}
        />
      )}

      {examReady && (
        <ExamReadyModal
          isOpen={examReady}
          onClose={closeGate}
          onStart={confirmStartExam}
          examLabel={selectedMockTask?.title || examMetaName || examName}
        />
      )}
    </div>
  );
};

const AiTutorSegment = ({aiTutorData, gradingData, examMeta}) => {
  const { themeName } = useTheme();
  const [isTutorActive, setIsTutorActive] = useState(false);

  useEffect(() => {
    const checkActive = () => {
      const raw = sessionStorage.getItem("bubbleai_tutor_context");
      setIsTutorActive(!!raw);
    };
    checkActive();
    const interval = setInterval(checkActive, 2000);
    return () => clearInterval(interval);
  }, []);

  const handleAskAi = () => {
    const aiContext = {
      gradingData,
      examName: examMeta?.examName || gradingData?.examName || "this exam",
      description: examMeta?.description || ""
    };
    sessionStorage.setItem("bubbleai_tutor_context", JSON.stringify(aiContext));
    window.open("/chat", "_blank");
  };

  return (
    <div style={{ gap: '1.5rem', display: 'flex', flexDirection: 'column', marginTop: '1rem' }}>
      <GlassCard theme={themeName} style={{ borderLeft: '4px solid #3E8F93' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <h6 style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', fontSize: '1.2rem', fontWeight: 800 }}>
              <FaRobot color="#3E8F93" /> Interactive AI Tutor
            </h6>
            <p style={{ opacity: 0.7, fontSize: '0.9rem', margin: 0 }}>Ask anything about your exam results or study materials.</p>
          </div>
          {isTutorActive ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', background: 'rgba(62, 143, 147, 0.1)', padding: '10px 20px', borderRadius: '30px', border: '1px solid rgba(62, 143, 147, 0.2)', backdropFilter: 'blur(8px)', boxShadow: '0 4px 15px rgba(62, 143, 147, 0.05)' }}>
              <OvalSpinner size={18} color="#3E8F93" />
              <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#3E8F93' }}>Ai Tutor active in another tab</span>
              <div 
                onClick={() => {
                  sessionStorage.removeItem("bubbleai_tutor_context");
                  setIsTutorActive(false);
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#ef5350';
                  e.currentTarget.style.transform = 'scale(1.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = 'rgba(62, 143, 147, 0.6)';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  cursor: 'pointer', 
                  marginLeft: '4px',
                  padding: '4px',
                  borderRadius: '50%',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  color: 'rgba(62, 143, 147, 0.6)'
                }}
                title="End AI Tutor Session"
              >
                <FaTimesCircle size={16} />
              </div>
            </div>
          ) : (
            <ButtonSubmitGreen onClick={handleAskAi} style={{ borderRadius: '30px', padding: '12px 24px' }}>
              <FaRobot style={{ marginRight: 8 }} /> Open Chat Assistant
            </ButtonSubmitGreen>
          )}
        </div>
      </GlassCard>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '1.5rem' }}>
        <GlassCard theme={themeName}>
          <h6 style={{ marginBottom: '1.5rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FaLightbulb color="#F7B801" /> Concept Explanations
          </h6>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {aiTutorData.failedConcepts?.length > 0 ? aiTutorData.failedConcepts.map((c, i) => (
              <div key={i} style={{ padding: '1.25rem', borderRadius: '16px', background: themeName === 'dark' ? 'rgba(255,255,255,0.03)' : '#f9f9f9', border: themeName === 'dark' ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.05)' }}>
                <div style={{ fontWeight: 800, marginBottom: '6px', color: '#3E8F93' }}>{c.topic}</div>
                <div style={{ fontSize: '0.9rem', lineHeight: '1.5', opacity: 0.8, color: themeName === 'dark' ? '#eee' : 'inherit' }}>{c.explanation}</div>
              </div>
            )) : (
              <p style={{ opacity: 0.6, fontStyle: 'italic' }}>No concepts identified for review. Great job!</p>
            )}
          </div>
        </GlassCard>

        <GlassCard theme={themeName}>
          <h6 style={{ marginBottom: '1.5rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FaTrophy color="#FFD700" /> Motivational Feedback
          </h6>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {aiTutorData.motivational?.map((m, i) => (
              <div key={i} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', padding: '12px', background: 'rgba(255, 215, 0, 0.05)', borderRadius: '12px', border: '1px solid rgba(255, 215, 0, 0.1)' }}>
                <div style={{ marginTop: '4px' }}><FaCheckCircle color="#FFD700" /></div>
                <div style={{ fontSize: '0.95rem', fontWeight: 500, fontStyle: 'italic' }}>"{m}"</div>
              </div>
            ))}
          </div>
        </GlassCard>
      </div>
    </div>
  );
};

const ExamInsightsSegment = ({examInsightsData}) => {
  const { themeName } = useTheme();
  return (
    <div style={{ gap: '1.5rem', display: 'flex', flexDirection: 'column', marginTop: '1rem' }}>
      <GlassCard theme={themeName}>
        <h6 style={{ marginBottom: '1rem', fontWeight: 800, fontSize: '1.1rem' }}>Exam Structure & Core Logistics</h6>
        <div style={{ padding: '1.5rem', borderRadius: '16px', background: themeName === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(62, 143, 147, 0.05)', marginBottom: '1.5rem', border: themeName === 'dark' ? '1px solid rgba(255,255,255,0.05)' : 'none' }}>
          <p style={{ margin: 0, lineHeight: '1.6', fontSize: '0.95rem', color: themeName === 'dark' ? '#eee' : 'inherit' }}>{examInsightsData.structure}</p>
        </div>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
          <StatCard theme={themeName} style={{ background: 'transparent', border: themeName === 'dark' ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.05)', boxShadow: 'none' }}>
            <div className="icon"><FaHourglass /></div>
            <div className="label">Exam Logistics</div>
            <ul style={{ padding: 0, listStyle: 'none', textAlign: 'left', fontSize: '0.8rem', marginTop: '10px', color: themeName === 'dark' ? '#ccc' : 'inherit' }}>
              {examInsightsData.logistics?.map((l, i) => <li key={i} style={{ marginBottom: '4px' }}>• {l}</li>)}
            </ul>
          </StatCard>
          <StatCard theme={themeName} style={{ background: 'transparent', border: themeName === 'dark' ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.05)', boxShadow: 'none' }}>
            <div className="icon"><FaCheckCircle /></div>
            <div className="label">Scoring Rubric</div>
            <div style={{ width: '100%', marginTop: '10px', color: themeName === 'dark' ? '#ccc' : 'inherit' }}>
              {examInsightsData.rubric?.map((r, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', marginBottom: '4px' }}>
                  <span>{r.section}</span>
                  <span style={{ fontWeight: 700 }}>{r.marks}m</span>
                </div>
              ))}
            </div>
          </StatCard>
        </div>
      </GlassCard>

      <GlassCard theme={themeName}>
        <h6 style={{ marginBottom: '1.5rem', fontWeight: 700 }}>{examInsightsData.trends?.[0]?.label ? "Performance Metrics" : "Historical Trends"}</h6>
        <div style={{ width: "100%", height: 300 }}>
          <ResponsiveContainer>
            <LineChart data={examInsightsData.trends}>
              <XAxis dataKey={examInsightsData.trends?.[0]?.label ? "label" : "year"} stroke={themeName === 'dark' ? 'rgba(255,255,255,0.5)' : '#666'} tick={{ fontSize: 10 }} />
              <YAxis domain={[0, 100]} stroke={themeName === 'dark' ? 'rgba(255,255,255,0.5)' : '#666'} tick={{ fontSize: 10 }} />
              <Tooltip contentStyle={{ 
                borderRadius: '12px', 
                border: 'none', 
                boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
                background: themeName === 'dark' ? '#1a1a1a' : '#fff',
                color: themeName === 'dark' ? '#fff' : '#000'
              }} itemStyle={{ color: themeName === 'dark' ? '#fff' : '#000' }} />
              <Line type="monotone" dataKey={examInsightsData.trends?.[0]?.label ? "score" : "avg"} stroke="#3E8F93" strokeWidth={4} dot={{ r: 6, fill: '#3E8F93' }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </GlassCard>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>
        <GlassCard theme={themeName} style={{ borderLeft: '4px solid #FF6B6B' }}>
          <h6 style={{ marginBottom: '1rem', fontWeight: 700, color: '#FF6B6B', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FaTimesCircle /> Common Pitfalls
          </h6>
          <ul style={{ paddingLeft: '1.25rem', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {examInsightsData.pitfalls?.map((p, i) => (
              <li key={i} style={{ fontSize: '0.9rem', opacity: 0.8 }}>{p}</li>
            ))}
          </ul>
        </GlassCard>

        <GlassCard theme={themeName}>
          <h6 style={{ marginBottom: '1rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FaMedal color="#FFD700" /> Success Stories
          </h6>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {examInsightsData.successStories?.map((s, i) => (
              <div key={i} style={{ padding: '12px', borderRadius: '12px', background: themeName === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)', border: themeName === 'dark' ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.05)' }}>
                <p style={{ fontSize: '0.85rem', fontStyle: 'italic', margin: '0 0 8px 0', color: themeName === 'dark' ? '#eee' : 'inherit' }}>"{s.quote}"</p>
                <div style={{ textAlign: 'right', fontSize: '0.75rem', fontWeight: 700, opacity: 0.6 }}>— {s.name}</div>
              </div>
            ))}
          </div>
        </GlassCard>
      </div>
    </div>
  );
};

const PeerBenchmarkingSegment = ({peerBenchmarkingData, themeName}) => {
  const [showOverlay, setShowOverlay] = useState(true);
  
  // If no data yet, show a placeholder or overlay
  const hasData = peerBenchmarkingData && Object.keys(peerBenchmarkingData).length > 0;
  const percentile = peerBenchmarkingData.percentile || 0;

  return (
    <div style={{ marginTop: '1rem' }}>
      {showOverlay && (
        <div style={{ position: 'relative', minHeight: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ position: 'absolute', inset: 0, filter: 'blur(8px)', opacity: 0.3, pointerEvents: 'none' }}>
             {/* Blurred background mockup */}
             <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', padding: '1rem' }}>
                <div style={{ height: '100px', background: '#ccc', borderRadius: '12px', opacity: 0.2 }} />
                <div style={{ height: '100px', background: '#ccc', borderRadius: '12px', opacity: 0.2 }} />
                <div style={{ height: '150px', background: '#ccc', gridColumn: 'span 2', borderRadius: '12px', opacity: 0.2 }} />
             </div>
          </div>
          <GlassCard theme={themeName} style={{ textAlign: 'center', padding: '3rem', maxWidth: '450px', zIndex: 10 }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🏆</div>
            <h4 style={{ fontWeight: 800, marginBottom: '1rem' }}>Global Standings</h4>
            <p style={{ opacity: 0.8, marginBottom: '2rem' }}>Ready to see how you rank among 1,000+ peers globally? We've prepared a detailed comparison based on speed and accuracy.</p>
            <ButtonSubmitGreen onClick={() => setShowOverlay(false)} style={{ borderRadius: '30px', padding: '15px 30px' }}>
              Reveal My Rankings
            </ButtonSubmitGreen>
          </GlassCard>
        </div>
      )}

      {!showOverlay && hasData && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          <StatGrid>
             <StatCard theme={themeName}>
                <div style={{ fontSize: '0.8rem', opacity: 0.6, marginBottom: '5px' }}>Percentile Ranking</div>
                <div style={{ fontSize: '2rem', fontWeight: 800, color: '#43AA8B' }}>{percentile}th</div>
                <div style={{ width: '100%', height: '4px', background: themeName === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)', borderRadius: '2px', marginTop: '10px' }}>
                   <div style={{ width: `${percentile}%`, height: '100%', background: '#43AA8B' }} />
                </div>
             </StatCard>
             <StatCard theme={themeName}>
                <div style={{ fontSize: '0.8rem', opacity: 0.6, marginBottom: '5px' }}>Global Rank</div>
                <div style={{ fontSize: '2rem', fontWeight: 800 }}>#{peerBenchmarkingData.rank}</div>
                <div style={{ fontSize: '0.75rem', opacity: 0.5 }}>out of 1,000 peers</div>
             </StatCard>
             <StatCard theme={themeName}>
                <div style={{ fontSize: '0.8rem', opacity: 0.6, marginBottom: '5px' }}>Speed Index</div>
                <div style={{ fontSize: '2rem', fontWeight: 800, color: '#F7B801' }}>{peerBenchmarkingData.userSpeed}</div>
                <div style={{ fontSize: '0.75rem', opacity: 0.5 }}>Peer Avg: {peerBenchmarkingData.avgSpeed}</div>
             </StatCard>
          </StatGrid>

          <GlassCard theme={themeName}>
            <h6 style={{ marginBottom: '1.5rem', fontWeight: 700 }}>Key Performance Benchmarks</h6>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {(peerBenchmarkingData.comparisons || peerBenchmarkingData.strengthComparison || [])?.map((s, i) => (
                <div key={i}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem', marginBottom: '8px' }}>
                    <span style={{ fontWeight: 600 }}>{s.label || s.topic}</span>
                    <span style={{ opacity: 0.7 }}>You: {s.user}% | Peers: {s.peer || s.peers}%</span>
                  </div>
                  <div style={{ width: '100%', height: '10px', background: themeName === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)', borderRadius: '5px', overflow: 'hidden', position: 'relative' }}>
                    <div style={{ position: 'absolute', height: '100%', left: 0, width: `${s.user}%`, background: '#3E8F93', zIndex: 2 }} />
                    <div style={{ position: 'absolute', height: '100%', left: 0, width: `${s.peer || s.peers}%`, background: themeName === 'dark' ? 'rgba(255,255,255,0.2)' : '#ccc', opacity: 0.5, zIndex: 1 }} />
                  </div>
                </div>
              ))}
            </div>
          </GlassCard>

          <GlassCard theme={themeName} style={{ background: themeName === 'dark' ? 'rgba(67, 170, 139, 0.1)' : 'rgba(67, 170, 139, 0.05)', borderLeft: '4px solid #43AA8B' }}>
             <h6 style={{ marginBottom: '10px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FaUsers color="#43AA8B" /> Community Insights
             </h6>
             <p style={{ fontSize: '0.95rem', lineHeight: '1.6', margin: 0, color: themeName === 'dark' ? '#eee' : 'inherit' }}>{peerBenchmarkingData.feedback || peerBenchmarkingData.communityInsights}</p>
          </GlassCard>

          <div style={{ textAlign: 'center', opacity: 0.5, fontSize: '0.8rem', padding: '1rem' }}>
            <FaLock style={{ marginRight: '5px' }} /> Global data is anonymized and simulated based on historical trends for your privacy.
          </div>
        </div>
      )}
      {!showOverlay && !hasData && (
        <div style={{ padding: 40, textAlign: 'center' }}>
          <p>Generating peer benchmarks based on your latest score...</p>
        </div>
      )}
    </div>
  );
};

const CareerAlignmentSegment = ({careerAlignmentData, themeName}) => (
  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem', marginTop: '1rem' }}>
    <GlassCard theme={themeName}>
      <h6 style={{ marginBottom: '1.2rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '10px' }}>
        <FaSuitcase color="#E67E22" /> Career Mapping
      </h6>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {careerAlignmentData.mapping?.map((c, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px', borderRadius: '10px', background: themeName === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(230, 126, 34, 0.05)', border: themeName === 'dark' ? '1px solid rgba(255,255,255,0.05)' : 'none' }}>
            <span style={{ fontWeight: 600 }}>{c.career}</span>
            <Badge variant="success" style={{ background: '#E67E22' }}>{c.match}% Fit</Badge>
          </div>
        ))}
      </div>
    </GlassCard>

    <GlassCard theme={themeName}>
      <h6 style={{ marginBottom: '1.2rem', fontWeight: 700 }}>Course Recommendations</h6>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {careerAlignmentData.courses?.map((c, i) => (
          <a key={i} href={c.link} target="_blank" rel="noreferrer" style={{ display: 'block', padding: '12px', borderRadius: '12px', border: themeName === 'dark' ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.05)', textDecoration: 'none', color: 'inherit', background: themeName === 'dark' ? 'rgba(255,255,255,0.01)' : 'transparent' }}>
            <div style={{ fontWeight: 700, color: '#E67E22', marginBottom: '4px' }}>{c.name}</div>
            <div style={{ fontSize: '0.8rem', opacity: 0.6, color: themeName === 'dark' ? '#ccc' : 'inherit' }}>{c.requirements}</div>
          </a>
        ))}
      </div>
    </GlassCard>

    <GlassCard theme={themeName} style={{ borderLeft: '4px solid #E67E22' }}>
      <h6 style={{ marginBottom: '1.2rem', fontWeight: 700 }}>Skills Assessment</h6>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
        {careerAlignmentData.skillGap?.map((s, i) => {
          const current = s.current || s.score || 0;
          const required = s.required || 100;
          return (
            <div key={i}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: '5px' }}>
                <span style={{ fontWeight: 600 }}>{s.skill}</span>
                <span style={{ opacity: 0.7 }}>{current}/{required}</span>
              </div>
              <ProgressBarTrack theme={themeName} height="8px">
                <ProgressBarFill progress={(current/required)*100} color={current >= required ? '#43AA8B' : '#F7B801'} />
              </ProgressBarTrack>
            </div>
          );
        })}
      </div>
    </GlassCard>
  </div>
);

const ProgressMonitoringSegment = ({progressMonitoringData, themeName}) => {
  const [showGoalModal, setShowGoalModal] = useState(false);
  const [goal, setGoal] = useState("");
  const mainColor = "#A259FF";

  const performance = progressMonitoringData?.performance || [
    { date: 'Initial', score: 0 },
    { date: 'Current', score: 0 }
  ];
  const milestones = progressMonitoringData?.milestones || [
    { text: "Complete your first mock to earn a badge!", date: "Upcoming" }
  ];
  const studyTime = progressMonitoringData?.studyTime || [
    { topic: "General", hours: 1 }
  ];
  const goals = progressMonitoringData?.goals || [
    { goal: "Score above 80% in the next test", done: false }
  ];
  const confidence = progressMonitoringData?.confidence || 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', marginTop: '1rem' }}>
      <GlassCard theme={themeName}>
        <h6 style={{ marginBottom: '1.5rem', fontWeight: 700 }}>Performance Dashboard</h6>
        <div style={{ width: "100%", height: 350 }}>
          <ResponsiveContainer>
            <LineChart data={performance}>
              <XAxis dataKey="date" stroke={themeName === 'dark' ? 'rgba(255,255,255,0.5)' : '#666'} tick={{fontSize: 12}} />
              <YAxis stroke={themeName === 'dark' ? 'rgba(255,255,255,0.5)' : '#666'} tick={{fontSize: 12}} />
              <Tooltip contentStyle={{ 
                borderRadius: '15px', 
                border: 'none', 
                boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
                background: themeName === 'dark' ? '#1a1a1a' : '#fff',
                color: themeName === 'dark' ? '#fff' : '#000'
              }} itemStyle={{ color: themeName === 'dark' ? '#fff' : '#000' }} />
              <Line type="monotone" dataKey="score" stroke={mainColor} strokeWidth={4} dot={{ r: 6, fill: mainColor, strokeWidth: 2, stroke: '#fff' }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </GlassCard>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>
        <GlassCard theme={themeName}>
          <h6 style={{ marginBottom: '1.2rem', fontWeight: 700 }}>Milestone Alerts</h6>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {milestones.map((m, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', borderRadius: '12px', background: themeName === 'dark' ? 'rgba(255, 215, 0, 0.1)' : 'rgba(255, 215, 0, 0.05)', border: '1px solid rgba(255, 215, 0, 0.2)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <FaTrophy color="#FFD700" />
                  <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>{m.text}</span>
                </div>
                <span style={{ fontSize: '0.75rem', opacity: 0.5 }}>{m.date}</span>
              </div>
            ))}
          </div>
        </GlassCard>

        <GlassCard theme={themeName}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.2rem' }}>
              <h6 style={{ margin: 0, fontWeight: 700 }}>Goals</h6>
              <ButtonThin onClick={() => setShowGoalModal(true)} style={{ padding: '4px 12px', fontSize: '0.75rem', borderRadius: '20px' }}>
                + Add Goal
              </ButtonThin>
           </div>
           <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
             {goals.map((g, i) => (
               <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.9rem', padding: '8px', borderRadius: '8px', background: g.done ? 'rgba(62, 143, 147, 0.05)' : 'transparent' }}>
                 {g.done ? <FaCheckCircle color="#3E8F93" /> : <FaTimesCircle color="#FF6B6B" opacity={0.3} />}
                 <span style={{ textDecoration: g.done ? 'line-through' : 'none', opacity: g.done ? 0.5 : 1 }}>{g.goal}</span>
               </div>
             ))}
           </div>
        </GlassCard>

        <GlassCard theme={themeName}>
          <h6 style={{ marginBottom: '1.2rem', fontWeight: 700 }}>Confidence Index</h6>
          <div style={{ textAlign: 'center', padding: '1rem' }}>
            <div style={{ fontSize: '3rem', fontWeight: 900, color: mainColor }}>{confidence}%</div>
            <p style={{ fontSize: '0.9rem', opacity: 0.6, marginTop: '10px' }}>Current Exam Readiness Score</p>
            <ProgressBarTrack theme={themeName} height="12px" style={{ marginTop: '20px' }}>
              <ProgressBarFill progress={confidence} color={mainColor} />
            </ProgressBarTrack>
          </div>
        </GlassCard>
      </div>

      {showGoalModal && (
        <PlainModalOverlay onClick={() => setShowGoalModal(false)}>
          <GlassCard theme={themeName} onClick={e => e.stopPropagation()} style={{ maxWidth: '400px', width: '90%', padding: '2rem' }}>
            <h4 style={{ fontWeight: 800, marginBottom: '1rem' }}>Set a New Goal</h4>
            <AuthInput
              type="text"
              value={goal}
              onChange={e => setGoal(e.target.value)}
              placeholder="e.g. Score 90% in next mock"
              style={{ marginBottom: '1.5rem' }}
            />
            <div style={{ display: 'flex', gap: '1rem' }}>
               <ButtonThin onClick={() => setShowGoalModal(false)} style={{ flex: 1 }}>Cancel</ButtonThin>
               <ButtonSubmitGreen onClick={() => setShowGoalModal(false)} disabled={!goal} style={{ flex: 1 }}>Save Goal</ButtonSubmitGreen>
            </div>
          </GlassCard>
        </PlainModalOverlay>
      )}

      <GlassCard theme={themeName}>
        <h6 style={{ marginBottom: '1.5rem', fontWeight: 700 }}>Study Time Analytics</h6>
        <div style={{ width: '100%', height: 350 }}>
          <ResponsiveContainer>
            <PieChart>
              <Pie
                data={studyTime}
                dataKey="hours"
                nameKey="topic"
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={5}
                label={({ name }) => name}
              >
                {studyTime.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={['#3E8F93', '#FF6B6B', '#6C63FF', '#F7B801'][index % 4]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </GlassCard>
      
      <div style={{ textAlign: 'center' }}>
        <ButtonThin style={{ padding: '12px 24px', borderRadius: '25px' }}>
          <FaDownload style={{ marginRight: 8 }} /> Download Progress Report (PDF)
        </ButtonThin>
      </div>
    </div>
  );
};


export default function InsightNTutor() {
  const { themeName } = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [authMenuOpen, setAuthMenuOpen] = useState(false);
  const [isActuallySubscribed, setIsActuallySubscribed] = useState(true);

  const [activeTab, setActiveTab] = useState("grading");
  const [gradingData, setGradingData] = useState({});
  const [examMeta, setExamMeta] = useState({});
  const [aiTutorData, setAiTutorData] = useState({});
  const [examInsightsData, setExamInsightsData] = useState({});
  const [peerBenchmarkingData, setPeerBenchmarkingData] = useState({});
  const [careerAlignmentData, setCareerAlignmentData] = useState({});
  const [progressMonitoringData, setProgressMonitoringData] = useState({});
  const [studyPlanData, setStudyPlanData] = useState({});
  const [showFullHeaderDescription, setShowFullHeaderDescription] = useState(false);
  
  const [segmentLoading, setSegmentLoading] = useState({ aiTutor:false, examInsights:false, careerAlignment:false, peerBenchmarking: false });
  const [segmentError, setSegmentError] = useState({ aiTutor:null, examInsights:null, careerAlignment:null, peerBenchmarking: null });

    const isAuth = localStorage?.getItem("token");
    const [isFeedbackTime, setIsFeedbackTime] = useState(false);
    const mobile = isMobile();

    useEffect(() => {
        let timer;
        if (aiTutorData && Object.keys(aiTutorData).length > 0 && !localStorage.getItem("feedbackTimeGeneratedMock")) {
            timer = setTimeout(() => {
                setIsFeedbackTime(true);
            }, 60000); // 1 minute after AI Tutor loads
        }
        return () => clearTimeout(timer);
    }, [aiTutorData]);

    const studyPlanTimerRef = useRef(null);
    const [studyPlanTimerElapsed, setStudyPlanTimerElapsed] = useState(false);

    const clearStudyPlanTimer = useCallback(() => {
      if (studyPlanTimerRef.current) {
        clearTimeout(studyPlanTimerRef.current);
        studyPlanTimerRef.current = null;
      }
      setStudyPlanTimerElapsed(false);
    }, []);
    const startStudyPlanTimer = useCallback(() => {
      clearStudyPlanTimer();
      studyPlanTimerRef.current = setTimeout(() => {
        setStudyPlanTimerElapsed(true);
        studyPlanTimerRef.current = null;
      }, 6000);
    }, [clearStudyPlanTimer]);

    useEffect(() => {
      return () => clearStudyPlanTimer();
    }, [clearStudyPlanTimer]);

    useEffect(() => {
      if (!mobile) {
        if (activeTab === 'studyPlan') startStudyPlanTimer(); else clearStudyPlanTimer();
      }
    }, [activeTab, mobile, startStudyPlanTimer, clearStudyPlanTimer]);

  // Always start InsightNTutor from the top of the page
  useEffect(() => {
    try {
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    } catch (_) {
      // no-op in non-browser environments
    }
  }, []);

  const errorSetter = useErrorSetter();
  const successSetter = useSuccessSetter();

  const HEADER_DESC_CHAR_LIMIT = 300;
  const headerDescription = examMeta?.description || '';
  const isLongHeaderDescription = headerDescription.length > HEADER_DESC_CHAR_LIMIT;
  const visibleHeaderDescription = (!isLongHeaderDescription || showFullHeaderDescription)
    ? headerDescription
    : `${headerDescription.slice(0, HEADER_DESC_CHAR_LIMIT).trimEnd()}…`;

  useEffect(() => {
    // Reset expanded state when exam description changes (navigating between exams)
    setShowFullHeaderDescription(false);
  }, [examMeta?.description]);

    const segmentComponents = {
      grading: (props) => <GradingSegment {...props} />,
      studyPlan: (props) => <StudyPlanSegment {...props} />,
      aiTutor: (props) => <AiTutorSegment {...props} />,
      examInsights: (props) => <ExamInsightsSegment {...props} />,
      peerBenchmarking: (props) => <PeerBenchmarkingSegment {...props} />,
      careerAlignment: (props) => <CareerAlignmentSegment {...props} />,
      progressMonitoring: (props) => <ProgressMonitoringSegment {...props} />
    };

    const segmentProps = {
      grading: { gradingData, examMeta, themeName },
      studyPlan: { studyPlanData, setStudyPlanData, errorSetter, successSetter, examName: examMeta?.examName || gradingData?.examName, examMetaName: examMeta?.subject || gradingData?.subject, studyPlanTimerElapsed, description: examMeta?.description, onRequestOpenGrading: () => { setActiveTab('grading'); }, themeName, isActuallySubscribed, setIsActuallySubscribed },
      aiTutor: { aiTutorData, gradingData, examMeta, themeName },
      examInsights: { examInsightsData, themeName },
      peerBenchmarking: { peerBenchmarkingData, themeName },
      careerAlignment: { careerAlignmentData, themeName },
      progressMonitoring: { progressMonitoringData, themeName }
    };

    const checkIfAuth = async () => {
        try {
            await checkAuthenticatedUser();
        } catch (error) {
            dispatch(setFetching(false));
            return navigate("/popin?mock");
        }
    };

          useEffect(() => {
            const fetchExamDetails = async (examId) => {
            dispatch(setExamDetails({}))
            try {
                dispatch(setFetching(true))
                const response = await axios.get(`/mock/get-exam-details?examId=${examId}`, {
                    headers: { "x-access-token": isAuth }
                });
                const data = response.data.examDetails || response.data.gradedExam || response.data.exam || {};
                setGradingData(data.grading || gradingData);
                setExamMeta(data.examMeta || {});
                setPeerBenchmarkingData(data.peerBenchmarking || peerBenchmarkingData);
                setProgressMonitoringData(data.progressMonitoring || progressMonitoringData);
                setStudyPlanData(data.studyPlan || studyPlanData);
                if (typeof response.data.isSubscribed !== 'undefined') setIsActuallySubscribed(!!response.data.isSubscribed);
                
                // If the initial fetch already has some data for segments, populate them
                if (data.aiTutor) setAiTutorData(data.aiTutor);
                if (data.examInsights) setExamInsightsData(data.examInsights);
                if (data.careerAlignment) setCareerAlignmentData(data.careerAlignment);

            } catch (err) {
                errorSetter(err?.response?.data?.message || err?.response?.data?.error || "Failed to fetch exam details. Please try again.");
                navigate("/user/dashboard/mock");
            } finally {
                dispatch(setFetching(false));
            }
        };

        checkIfAuth();
        const urlParams = new URLSearchParams(window.location.search);
        const examId = urlParams.get("examId");

        if (examId && examId !== "") {
            fetchExamDetails(examId);
        } else {
            navigate("/user/dashboard/mock");
        }
        // eslint-disable-next-line
    }, []);

    const toggleAuthMenu = () => {
      setAuthMenuOpen(!authMenuOpen);
    };

  const handleLazyFetch = async (segmentKey) => {
    if (!['aiTutor','examInsights','careerAlignment','peerBenchmarking'].includes(segmentKey)) return;
    if (segmentLoading[segmentKey]) return; 
    const existing = segmentKey === 'aiTutor' ? aiTutorData : segmentKey === 'examInsights' ? examInsightsData : segmentKey === 'careerAlignment' ? careerAlignmentData : peerBenchmarkingData;
    if (existing && Object.keys(existing).length > 0) return;
    const examId = new URLSearchParams(window.location.search).get('examId');
    if (!examId) return;
    setSegmentLoading(prev => ({ ...prev, [segmentKey]: true }));
    setSegmentError(prev => ({ ...prev, [segmentKey]: null }));
    try {
      const response = await axios.get(`/mock/get-exam-segment?examId=${examId}&segment=${segmentKey}`, { headers: { 'x-access-token': isAuth } });
      const payload = response.data.data || {};
      if (segmentKey === 'aiTutor') setAiTutorData(payload);
      else if (segmentKey === 'examInsights') setExamInsightsData(payload);
      else if (segmentKey === 'careerAlignment') setCareerAlignmentData(payload);
      else if (segmentKey === 'peerBenchmarking') setPeerBenchmarkingData(payload);
    } catch (err) {
      if (err?.response?.data?.openPricing) {
        window.dispatchEvent(new CustomEvent('openExamPricing'));
      }
      setSegmentError(prev => ({ ...prev, [segmentKey]: err?.response?.data?.message || err?.response?.data?.error || 'Failed to load segment.' }));
    } finally {
      setSegmentLoading(prev => ({ ...prev, [segmentKey]: false }));
    }
  };


  return (
    <div >
        <BubbleFloat />
        <UpgradePopup />

        <div className="auth-container-inner">
            <AuthHeader
                authMenuOpen={authMenuOpen}
                onClick={toggleAuthMenu}
                headerText="MOCK Insights"
            />
            <AuthSideMenu opened={authMenuOpen} arrayDetails={[]} onSwitch={() => {}} />

            <div>
                <Container>
                    <AIHeaderCard>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div>
                            <h2 style={{ fontSize: '1.8rem', fontWeight: 800, margin: 0 }}>
                              {examMeta?.examName || gradingData?.examName || 'Exam Results'}
                            </h2>
                            <p style={{ opacity: 0.9, marginTop: '5px', fontSize: '1.1rem' }}>
                              {examMeta?.subject || gradingData?.subject || 'Assessment Summary'}
                            </p>
                          </div>
                          {!mobile && gradingData.score && (
                            <div style={{ textAlign: 'right' }}>
                              <div style={{ fontSize: '2.5rem', fontWeight: 900 }}>{gradingData.percentage}%</div>
                              <div style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '1px' }}>Global Percentile</div>
                            </div>
                          )}
                        </div>
                        {headerDescription && (
                          <div style={{ marginTop: '0.75rem', maxWidth: '860px' }}>
                            <div
                              style={{
                                fontSize: '0.9rem',
                                lineHeight: '1.6',
                                opacity: 0.9,
                                marginBottom: isLongHeaderDescription ? 8 : 0,
                                maxHeight: isLongHeaderDescription && !showFullHeaderDescription ? '4.8em' : 'none',
                                overflow: isLongHeaderDescription && !showFullHeaderDescription ? 'hidden' : 'visible',
                                transition: isLongHeaderDescription ? 'max-height 0.35s ease' : 'none',
                              }}
                            >
                              {formatLargeText(visibleHeaderDescription)}
                            </div>
                            {isLongHeaderDescription && (
                              <button
                                type="button"
                                onClick={() => setShowFullHeaderDescription(prev => !prev)}
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 8,
                                  padding: '6px 14px',
                                  borderRadius: 999,
                                  border: '1px solid rgba(95,190,197,0.6)',
                                  background: themeName === 'dark'
                                    ? 'linear-gradient(90deg, rgba(95,190,197,0.24), rgba(162,89,255,0.26))'
                                    : 'linear-gradient(90deg, rgba(95,190,197,0.1), rgba(162,89,255,0.14))',
                                  color: themeName === 'dark' ? '#e5f9ff' : '#0f3135',
                                  fontSize: '0.78rem',
                                  fontWeight: 600,
                                  letterSpacing: '0.06em',
                                  textTransform: 'uppercase',
                                  cursor: 'pointer',
                                  boxShadow: '0 8px 20px rgba(95,190,197,0.25)',
                                  backdropFilter: 'blur(10px)',
                                  outline: 'none'
                                }}
                              >
                                <span>{showFullHeaderDescription ? 'Show less' : 'See more'}</span>
                                <span
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 18,
                                    height: 18,
                                    borderRadius: '50%',
                                    background: themeName === 'dark' ? 'rgba(11,34,41,0.9)' : 'rgba(255,255,255,0.9)',
                                    color: themeName === 'dark' ? '#5Fbec5' : '#3E8F93',
                                  }}
                                >
                                  {showFullHeaderDescription ? <FaChevronUp size={10} /> : <FaChevronDown size={10} />}
                                </span>
                              </button>
                            )}
                          </div>
                        )}
                        <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                          <Badge variant={gradingData.pass ? 'success' : 'danger'}>
                            {gradingData.pass ? 'PASS' : 'FAIL'}
                          </Badge>
                          <span style={{ fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <FaCalendarAlt /> Completed on {examMeta?.dateTaken ? new Date(examMeta.dateTaken).toLocaleDateString() : new Date().toLocaleDateString()}
                          </span>
                        </div>
                    </AIHeaderCard>

                    <div style={{ marginBottom: '1rem' }}>
                      <TabNavigation>
                        {segments.map(seg => (
                          <TabItem
                            key={seg.key}
                            active={activeTab === seg.key}
                            onClick={() => {
                              setActiveTab(seg.key);
                              handleLazyFetch(seg.key);
                            }}
                          >
                            {seg.icon} {seg.label}
                          </TabItem>
                        ))}
                      </TabNavigation>
                    </div>

                    <div className={mockCss.InsightNTutorWrapper} style={{ border: 'none', background: 'transparent', padding: 0 }}>
                        <div className={mockCss.TabContent} style={{ width: '100%', padding: 0 }}>
                          {['aiTutor','examInsights','careerAlignment','peerBenchmarking'].includes(activeTab) && segmentLoading[activeTab] && (
                            <GlassCard style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '60px 0' }}>
                              <Vortex
                                visible={true}
                                height={100}
                                width={100}
                                ariaLabel="segment-loading"
                                colors={["#4A90E2", "#A259FF", "#4A90E2", "#A259FF", "#4A90E2", "#A259FF"]}
                              />
                              <p style={{ marginTop: 20, fontSize: '1rem', fontWeight: 600 }}>AI is analyzing your results...</p>
                            </GlassCard>
                          )}
                          {segmentError[activeTab] && (
                            <GlassCard style={{ textAlign: 'center', padding: '40px' }}>
                              <p style={{ color: '#FF6B6B', marginBottom: '1rem' }}>{segmentError[activeTab]}</p>
                              <ButtonThin onClick={() => handleLazyFetch(activeTab)}>Try Again</ButtonThin>
                            </GlassCard>
                          )}
                          {!segmentLoading[activeTab] && !segmentError[activeTab] && (
                            <>
                              <div style={{ marginBottom: '1.5rem', padding: '1.2rem', borderRadius: '15px', background: themeName === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)', border: themeName === 'dark' ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.03)' }}>
                                <p style={{ fontSize: '0.95rem', lineHeight: '1.6', opacity: 0.8, color: themeName === 'dark' ? '#eee' : '#444', margin: 0 }}>
                                  {segments.find(s => s.key === activeTab)?.description}
                                </p>
                              </div>
                              {segmentComponents[activeTab](segmentProps[activeTab])}
                            </>
                          )}
                        </div>
                    </div>
                </Container>
            </div>
            {isFeedbackTime && (
                <Feedback origin="insightnTutor" onClose={() => {
                    setIsFeedbackTime(false);
                    localStorage.setItem("feedbackTimeGeneratedMock", "true");
                }} />
            )}
        </div>
    </div>
  );
}

